import { useSelector } from 'react-redux';
import { ReactNode } from 'react';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { useLogger, useRouter, useServices } from '@packages/shared/core/common/hooks';
import { joinUrls } from '@packages/shared/core/common/utils/url';
import { SignUpEmailDataHelpers, SignUpGoogleResponseData } from '@packages/shared/core/auth/entities';
import { getSignUpError, getUserPreferences } from '@packages/shared/core/user/store/selectors';
import { ApiError } from '@packages/shared/core/common/errors';
import { QuizScreenProps, SignUpStepData } from '@packages/shared/core/quiz/entities';
import { useExperts } from '@packages/shared/core/experts/hooks';

type SignUpStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const SignUpStep = ({ id, renderQuizScreen }: SignUpStepProps) => {
  const logger = useLogger();

  const router = useRouter();

  const { analyticsService, authService } = useServices();

  const {
    back,
    next,
    stepData,
    signUpWithGoogle,
    totalSteps,
    stepOrder,
    baseUrl,
    userPreferences,
    flowName,
    updateUserPreferences,
  } = useQuizStep(id);

  const { signUpByGoogleStatus } = useSelector(getUserPreferences);
  const errorMessage = useSelector(getSignUpError);
  const { selectedExpert } = useExperts({ flowName });

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const goal = (stepData as SignUpStepData).getGoal(userPreferences.goal);

  const goBack = async () => back();

  const handleSubmit = async (data: { email: string }, { setErrors }: SignUpEmailDataHelpers) => {
    try {
      analyticsService.registrationFormClicked('common');

      const isValidEmailPromise = authService.isValidEmail(data.email);
      const doesEmailExistsPromise = authService.doesEmailExists(data.email);

      const [doesEmailExist] = await Promise.all([doesEmailExistsPromise, isValidEmailPromise]);

      updateUserPreferences('email', data.email);
      updateUserPreferences('emailAlreadyExists', doesEmailExist.exists);

      await next();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors({ email: err.originalError.message });
      } else {
        setErrors({ email: 'Something went wrong. Try again later' });
      }
    }
  };

  const doSignUpWithGoogle = async (data: SignUpGoogleResponseData) => {
    analyticsService.registrationFormClicked('google');
    await signUpWithGoogle(data);
    return router.push(joinUrls(baseUrl, 'phoneNumber'));
  };

  return (
    <>
      {renderQuizScreen({
        onBack: goBack,
        goNext: handleSubmit,
        errorMessage,
        signUpWithGoogle: doSignUpWithGoogle,
        signUpByGoogleStatus,
        totalSteps,
        goal,
        stepOrder,
        expert: selectedExpert,
        ...(stepData as SignUpStepData),
      })}
    </>
  );
};

export default SignUpStep;
